import { useColorMode } from "@/components/layouts/ColorModeContext";
import { resolvePlace } from "@/features/geocoders";
import type { store } from "@/store";
import {
  climateMetricsApi,
  fetchClimateMetricsLayer,
  fetchClimateMetricsSlider,
  isClimateMetricsBuildRetired,
  loadedClimateMetricsSlider,
  subscribeClimateMetricsBuildRetired,
  useGetClimateMetricsBuildQuery,
  useGetClimateMetricsSummaryQuery,
} from "@/store/services/climateMetrics";
import { skipToken } from "@reduxjs/toolkit/query";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { parseLatLon } from "./format";
import { baselineScenario, initialSelection, MAX_POINTS, normalise, type Layout, type Selection } from "./model";
import type { Metric, Place, Point, Summary } from "./types";

/**
 * The selection as stored in the URL and in localStorage: horizons by id, so a link still works
 * for a build whose horizons are ordered differently.
 */
interface Stored {
  hazard?: string;
  metric?: string;
  vt?: string;
  scen?: string;
  h?: string;
  points?: Point[];
  sel?: number;
  layout?: string;
  tScen?: string;
  tH?: string;
}

const STORAGE_KEY = "hub.climateMetrics.selection.v2";
const COORDINATES_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const QUERY_KEYS = ["hazard", "metric", "vt", "scen", "h", "points", "sel", "layout", "tscen", "th"] as const;
const LAYOUTS: Layout[] = ["year", "ssp", "all"];

const one = (value: string | string[] | undefined) => (Array.isArray(value) ? value[0] : value);

/** Points as `lat,lon,name[,placeId]` joined by `;`, each field URI-encoded. */
function encodePoints(points: Point[]): string {
  return points
    .map((p) =>
      [p.lat.toFixed(4), p.lon.toFixed(4), p.name, ...(p.placeId ? [p.placeId] : [])].map(encodeURIComponent).join(",")
    )
    .join(";");
}

/**
 * The points a link names. A point whose fields do not decode (a truncated or hand-edited link),
 * whose name is empty, or whose coordinates are missing or off the globe is dropped.
 */
export function decodePoints(text: string | undefined, resolvedAt: number): Point[] {
  if (!text) return [];
  const fields = (part: string) => {
    try {
      return part.split(",").map(decodeURIComponent);
    } catch {
      return [];
    }
  };
  return text
    .split(";")
    .map(fields)
    .map(([lat, lon, name, placeId]) => ({ name, placeId, at: parseLatLon(`${lat},${lon}`) }))
    .filter(({ name, at }) => name && at)
    .slice(0, MAX_POINTS)
    .map(({ name, placeId, at }) => ({ name, ...at!, ...(placeId ? { placeId, resolvedAt } : {}) }));
}

function fromQuery(query: Record<string, string | string[] | undefined>): Stored | null {
  if (!QUERY_KEYS.some((key) => query[key] !== undefined)) return null;
  const sel = one(query.sel);
  return {
    hazard: one(query.hazard),
    metric: one(query.metric),
    vt: one(query.vt),
    scen: one(query.scen),
    h: one(query.h),
    points: decodePoints(one(query.points), Date.now()),
    sel: sel === undefined ? undefined : Number(sel),
    layout: one(query.layout),
    tScen: one(query.tscen),
    tH: one(query.th),
  };
}

function fromStorage(): Stored | null {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "null");
    return saved && typeof saved === "object" ? saved : null;
  } catch {
    return null;
  }
}

function toStored(summary: Summary, s: Selection): Stored {
  return { ...s, h: summary.horizons[s.h].id, tH: summary.horizons[s.tH].id };
}

function toSelection(summary: Summary, stored: Stored): Selection {
  const start = initialSelection(summary);
  const horizon = (id: string | undefined, fallback: number) => {
    const k = summary.horizons.findIndex((h) => h.id === id);
    return k < 0 ? fallback : k;
  };
  const selection: Selection = {
    hazard: stored.hazard ?? start.hazard,
    metric: stored.metric ?? (stored.hazard ? "" : start.metric),
    vt: stored.vt === "diff" ? "diff" : "value",
    scen: stored.scen ?? start.scen,
    h: horizon(stored.h, start.h),
    points: Array.isArray(stored.points) ? stored.points : [],
    sel: Number.isInteger(stored.sel) ? stored.sel! : (stored.points?.length ?? 0) - 1,
    layout: LAYOUTS.includes(stored.layout as Layout) ? (stored.layout as Layout) : start.layout,
    tScen: stored.tScen ?? stored.scen ?? start.tScen,
    tH: horizon(stored.tH, start.tH),
  };
  return normalise(summary, selection);
}

function save(stored: Stored) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Private windows and blocked storage: the URL still carries the selection.
  }
}

/**
 * The selection, read once from the URL (or, when the URL has none, from the last visit), and a
 * setter that keeps it consistent with the build and writes it back to both. Coordinates from
 * Google saved more than 30 days ago are looked up again.
 */
export function useSelection(
  summary: Summary | undefined
): [Selection | null, (change: Partial<Selection> | ((s: Selection) => Selection)) => void] {
  const router = useRouter();
  const [selection, setSelection] = useState<Selection | null>(null);

  useEffect(() => {
    if (!router.isReady || !summary || selection) return;
    const initial = toSelection(summary, fromQuery(router.query) ?? fromStorage() ?? {});
    setSelection(initial);
    const stale = initial.points.filter((p) => p.placeId && !(Date.now() - (p.resolvedAt ?? 0) < COORDINATES_TTL_MS));
    if (!stale.length) return;
    Promise.all(stale.map((p) => resolvePlace(p.placeId!, p.name))).then((fresh) => {
      setSelection(
        (current) =>
          current && {
            ...current,
            points: current.points.map((p) => {
              const found = fresh.find((f) => f && f.placeId === p.placeId);
              return found ? { ...p, lat: found.lat, lon: found.lon, resolvedAt: Date.now() } : p;
            }),
          }
      );
    });
  }, [router.isReady, router.query, summary, selection]);

  useEffect(() => {
    if (!selection || !summary) return;
    const stored = toStored(summary, selection);
    save(stored);
    const query = { ...router.query };
    for (const key of QUERY_KEYS) delete query[key];
    Object.assign(query, {
      hazard: stored.hazard,
      metric: stored.metric,
      vt: stored.vt,
      scen: stored.scen,
      h: stored.h,
      layout: stored.layout,
      tscen: stored.tScen,
      th: stored.tH,
      ...(selection.points.length ? { points: encodePoints(selection.points), sel: String(selection.sel) } : {}),
    });
    router.replace({ pathname: router.pathname, query }, undefined, { shallow: true, scroll: false });
    // The router object changes on every navigation; only the selection should trigger a write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  const update = useCallback(
    (change: Partial<Selection> | ((s: Selection) => Selection)) => {
      setSelection((current) => {
        if (!current || !summary) return current;
        return normalise(summary, typeof change === "function" ? change(current) : { ...current, ...change });
      });
    },
    [summary]
  );
  return [selection, update];
}

/**
 * The build this deployment serves and its summary. The build id comes first, so place requests
 * can start alongside the summary instead of after it.
 */
export function useSummary() {
  const build = useGetClimateMetricsBuildQuery();
  const summary = useGetClimateMetricsSummaryQuery(build.data?.build ?? skipToken);
  return {
    build: build.data?.build,
    summary: summary.data,
    error: build.error ?? summary.error,
    isLoading: build.isLoading || summary.isLoading,
  };
}

/** Whether the Worker has answered that this page's build is no longer served, so only a reload helps. */
export function useBuildRetired(): boolean {
  return useSyncExternalStore(subscribeClimateMetricsBuildRetired, isClimateMetricsBuildRetired, () => false);
}

export interface PointPlace {
  point: Point;
  place: Place | undefined;
  isLoading: boolean;
  isError: boolean;
}

/** The store as the place cache sees it; the store's own type does not list its API slices. */
type PlaceState = Parameters<ReturnType<typeof climateMetricsApi.endpoints.getClimateMetricsPlace.select>>[0];

const placeArg = (build: string, point: Point) => ({
  build,
  lat: Math.round(point.lat * 1e4) / 1e4,
  lon: Math.round(point.lon * 1e4) / 1e4,
});

/** Every metric at each point, one request per point, shared through the store's cache. */
export function usePlaces(build: string | undefined, points: Point[]): PointPlace[] {
  const dispatch = useDispatch<typeof store.dispatch>();
  const args = useMemo(() => (build ? points.map((p) => placeArg(build, p)) : []), [build, points]);
  useEffect(() => {
    const subscriptions = args.map((arg) => dispatch(climateMetricsApi.endpoints.getClimateMetricsPlace.initiate(arg)));
    return () => subscriptions.forEach((s) => s.unsubscribe());
  }, [dispatch, args]);
  const selectors = useMemo(
    () => args.map((arg) => climateMetricsApi.endpoints.getClimateMetricsPlace.select(arg)),
    [args]
  );
  const results = useSelector((state: PlaceState) => selectors.map((select) => select(state)), shallowEqual);
  return points.map((point, i) => ({
    point,
    place: results[i]?.data,
    isLoading: !results[i] || results[i].isUninitialized || results[i].isLoading,
    isError: !!results[i]?.isError,
  }));
}

/**
 * The layer the map shows: the baseline, or one horizon of the selected value type and scenario.
 * Off the baseline the whole slider is fetched, so dragging the slider redraws from memory; until
 * it arrives the single layer is fetched alongside it, which comes much sooner. `layer` is
 * undefined while loading and null where `reason` says there is no map.
 */
export function useMapLayer(
  build: string,
  summary: Summary,
  m: Metric,
  s: Selection,
  reason: string
): { layer: Float32Array | null | undefined; error: boolean } {
  const horizon = summary.horizons[s.h].id;
  const base = baselineScenario(summary, m);
  const key = [m.id, s.vt, s.scen, s.h, reason].join("/");
  const ready = !reason && s.h > 0 ? loadedClimateMetricsSlider(build, m.id, s.vt, s.scen) : undefined;
  const [state, setState] = useState<{ key: string; layer?: Float32Array | null; error: boolean }>({
    key: "",
    error: false,
  });

  useEffect(() => {
    if (reason || ready) return;
    let live = true;
    let final = false;
    const show = (layer: Float32Array | null, last: boolean) => {
      if (!live || final) return;
      final = last;
      setState({ key, layer, error: false });
    };
    const fail = () => live && !final && setState({ key, error: true });
    if (s.h === 0) {
      if (base) fetchClimateMetricsLayer(build, m.id, "value", horizon, base).then((l) => show(l, true), fail);
      else show(null, true);
    } else {
      // Without the slider the single layer is the map; the map fails only when both do.
      let single: "pending" | "shown" | "failed" = "pending";
      let sliderFailed = false;
      fetchClimateMetricsSlider(build, m.id, s.vt, s.scen).then(
        (slider) => show(slider.layer(s.h), true),
        () => {
          sliderFailed = true;
          if (single === "shown") final = true;
          else if (single === "failed") fail();
        }
      );
      fetchClimateMetricsLayer(build, m.id, s.vt, horizon, s.scen).then(
        (l) => {
          single = "shown";
          show(l, sliderFailed);
        },
        () => {
          single = "failed";
          if (sliderFailed) fail();
        }
      );
    }
    return () => {
      live = false;
    };
  }, [build, m.id, s.vt, s.scen, s.h, horizon, base, key, reason, ready]);

  if (reason) return { layer: null, error: false };
  if (ready) return { layer: ready.layer(s.h), error: false };
  return state.key === key ? { layer: state.layer, error: state.error } : { layer: undefined, error: false };
}

/** Each cell's country index + 1, north-up; 0 for none. Builds made before summaries carried cells have none. */
export const decodeCountryCells = (cells: string | undefined): Uint8Array =>
  Uint8Array.from(atob(cells ?? ""), (c) => c.charCodeAt(0));

export const useCountryCells = (summary: Summary): Uint8Array =>
  useMemo(() => decodeCountryCells(summary.countries.cells), [summary]);

/** The land mask, north-up, one byte per cell. */
export function useLand(summary: Summary): Uint8Array {
  return useMemo(() => {
    const bits = Uint8Array.from(atob(summary.landmask), (c) => c.charCodeAt(0));
    const out = new Uint8Array(summary.grid.rows * summary.grid.cols);
    for (let i = 0; i < out.length; i++) out[i] = (bits[i >> 3] >> (7 - (i & 7))) & 1;
    return out;
  }, [summary]);
}

export const useDark = () => useColorMode().colorMode === "dark";
